import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import {
  getTenantBrandingApi,
  updateTenantBrandingApi,
} from "../apis/brandingApis";
import { errorMessage, successMessage } from "../../../utils";

const initialFormData = {
  name: "",
  logo: "",
  square_logo: "",
  footer_logo: "",
  favicon: "",
  email_logo: "",
  pos_receipt_logo: "",
};

export const useBrandingConfig = () => {
  const [, setForceUpdate] = useState(0);
  const [state, setState] = useImmer({
    formData: initialFormData,
    previews: {},
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
  });
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await getTenantBrandingApi();
        if (response?.data) {
          setState((draft) => {
            draft.formData = { ...initialFormData, ...response.data };
            draft.previews = {};
          });
        } else {
          errorMessage(response?.message || "Failed to load branding settings");
        }
      } catch (error) {
        errorMessage(
          error.response?.data?.message ||
            error.message ||
            "Failed to load branding settings",
        );
      } finally {
        setState((draft) => {
          draft.isLoading = false;
        });
      }
    };

    fetchBranding();
  }, [setState]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.formData[name] = value;
      draft.hasChanges = true;
    });
  };

  const onAssetChange = (event, fieldName) => {
    const [file] = event.target.files;
    if (!file) return;

    if (fieldName === "favicon") {
      if (!/\.(png|svg|ico)$/i.test(file.name)) {
        errorMessage("Only PNG, SVG, and ICO files are allowed for favicon.");
        event.target.value = "";
        return;
      }
    } else {
      if (!/\.(png|svg)$/i.test(file.name)) {
        errorMessage("Only PNG and SVG files are allowed.");
        event.target.value = "";
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      setState((draft) => {
        draft.formData[fieldName] = file;
        draft.previews[fieldName] = reader.result;
        draft.hasChanges = true;
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!formValidator.current.allValid()) {
      formValidator.current.showMessages();
      setForceUpdate((previous) => previous + 1);
      return;
    }

    try {
      setState((draft) => {
        draft.isSubmitting = true;
      });

      const payload = new FormData();
      Object.entries(state.formData).forEach(([fieldName, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          payload.append(fieldName, value);
        }
      });

      const response = await updateTenantBrandingApi(payload);

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
          draft.hasChanges = false;
        });
        successMessage(
          response.message || "Branding settings updated successfully",
        );
      } else {
        errorMessage(response?.message || "Failed to update branding settings");
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ||
          error.message ||
          "Failed to update branding settings",
      );
    } finally {
      setState((draft) => {
        draft.isSubmitting = false;
      });
    }
  };

  return { state, formValidator, onChange, onAssetChange, onSubmit };
};
